import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Loader2, Plus, Edit, Trash2, Eye, EyeOff, RefreshCw, X
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../AuthContext';
import { adminApi, roomTypesApi } from '../../services/api';
import { supabase } from '../../supabaseClient';
import ImageManager from '../../components/ImageManager';

const AdminRoomsPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [rooms, setRooms] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [token, setToken] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    short_description: '',
    room_type_id: '',
    price_usd: '',
    price_zar: '',
    price_gbp: '',
    price_eur: '',
    max_occupancy: 2,
    beds: '',
    size_sqm: '',
    images: [],
    amenities: '',
    available: true
  });

  const fetchRooms = useCallback(async () => {
    try {
      setLoading(true);
      const { data: sessionData } = await supabase.auth.getSession();
      const authToken = sessionData?.session?.access_token;
      
      if (!authToken) {
        navigate('/login');
        return;
      }
      
      setToken(authToken);
      
      // Fetch rooms and room types in parallel
      const [roomsData, roomTypesData] = await Promise.all([
        adminApi.getRooms(authToken),
        roomTypesApi.getAll(false)
      ]);
      
      setRooms(roomsData);
      setRoomTypes(roomTypesData);
    } catch (err) {
      console.error('Error fetching rooms:', err);
      if (err.message.includes('403')) {
        navigate('/admin');
      }
      toast({
        title: "Error",
        description: err.message || "Failed to load rooms",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [navigate, toast]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchRooms();
  }, [isAuthenticated, navigate, fetchRooms]);

  const handleEdit = (room) => {
    setEditing(room.id);
    setFormData({
      name: room.name || '',
      slug: room.slug || '',
      description: room.description || '',
      short_description: room.short_description || '',
      room_type_id: room.room_type_id || '',
      price_usd: room.price_usd || '',
      price_zar: room.price_zar || '',
      price_gbp: room.price_gbp || '',
      price_eur: room.price_eur || '',
      max_occupancy: room.max_occupancy || 2,
      beds: room.beds || '',
      size_sqm: room.size_sqm || '',
      images: room.images || [],
      amenities: (room.amenities || []).join(', '),
      available: room.available !== false
    });
    setShowForm(true);
  };

  const handleCreate = () => {
    setEditing(null);
    setFormData({
      name: '',
      slug: '',
      description: '',
      short_description: '',
      room_type_id: '',
      price_usd: '',
      price_zar: '',
      price_gbp: '',
      price_eur: '',
      max_occupancy: 2,
      beds: '',
      size_sqm: '',
      images: [],
      amenities: '',
      available: true
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditing(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.images.length === 0) {
      toast({
        title: "Validation Error",
        description: "Please add at least one image for the room",
        variant: "destructive"
      });
      return;
    }
    
    try {
      setSaving(true);
      const { data: sessionData } = await supabase.auth.getSession();
      const authToken = sessionData?.session?.access_token;
      
      const roomData = {
        name: formData.name,
        slug: formData.slug || formData.name.toLowerCase().replace(/\s+/g, '-'),
        description: formData.description,
        short_description: formData.short_description,
        room_type_id: formData.room_type_id || null,
        price_usd: parseFloat(formData.price_usd) || 0,
        price_zar: parseFloat(formData.price_zar) || 0,
        price_gbp: parseFloat(formData.price_gbp) || 0,
        price_eur: parseFloat(formData.price_eur) || 0,
        max_occupancy: parseInt(formData.max_occupancy) || 2,
        beds: formData.beds,
        size_sqm: parseInt(formData.size_sqm) || 0,
        images: formData.images,
        amenities: formData.amenities.split(',').map(s => s.trim()).filter(Boolean),
        available: formData.available
      };
      
      if (editing) {
        await adminApi.updateRoom(editing, roomData, authToken);
        toast({ title: "Success", description: "Room updated successfully" });
      } else {
        await adminApi.createRoom(roomData, authToken);
        toast({ title: "Success", description: "Room created successfully" });
      }
      
      setShowForm(false);
      setEditing(null);
      fetchRooms();
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "Failed to save room",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (roomId) => {
    if (!window.confirm('Are you sure you want to delete this room?')) return;
    
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const authToken = sessionData?.session?.access_token;
      
      await adminApi.deleteRoom(roomId, authToken);
      toast({ title: "Success", description: "Room deleted successfully" });
      fetchRooms();
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "Failed to delete room",
        variant: "destructive"
      });
    }
  };

  const toggleAvailability = async (room) => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const authToken = sessionData?.session?.access_token;
      
      await adminApi.updateRoom(room.id, { available: !room.available }, authToken);
      toast({ 
        title: "Success", 
        description: `Room ${room.available ? 'hidden' : 'made available'}` 
      });
      fetchRooms();
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "Failed to update room",
        variant: "destructive"
      });
    }
  };

  if (!isAuthenticated) return null;

  return (
    <div style={{ backgroundColor: 'var(--bg-primary)', minHeight: '100vh', paddingTop: '80px' }} data-testid="admin-rooms">
      <div className="container py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/admin">
            <Button variant="outline" size="icon" style={{ borderRadius: 0 }}>
              <ArrowLeft size={20} />
            </Button>
          </Link>
          <div className="flex-1">
            <h1 className="hero-medium">Manage Rooms</h1>
          </div>
          <Button className="btn-primary flex items-center gap-2" onClick={handleCreate} data-testid="add-room-btn">
            <Plus size={16} />
            Add Room
          </Button>
        </div>

        {/* Form Dialog */}
        <Dialog open={showForm} onOpenChange={(open) => { if (!open) handleCancel(); }}>
          <DialogContent className="sm:max-w-2xl" style={{ borderRadius: 0, maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 0 }}>
            <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4" style={{ borderBottom: '1px solid var(--border-light)' }}>
              <DialogTitle>{editing ? 'Edit Room' : 'Create New Room'}</DialogTitle>
              <DialogDescription>Fill in the room details below.</DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name">Room Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    style={{ borderRadius: 0 }}
                    data-testid="room-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="slug">Slug (URL-friendly name)</Label>
                  <Input
                    id="slug"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    placeholder="auto-generated if empty"
                    style={{ borderRadius: 0 }}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="short_description">Short Description</Label>
                <Input
                  id="short_description"
                  value={formData.short_description}
                  onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
                  placeholder="Brief description for cards"
                  style={{ borderRadius: 0 }}
                />
              </div>

              {/* Room Type Selection */}
              <div>
                <Label htmlFor="room_type_id">Room Type (for billing)</Label>
                <select
                  id="room_type_id"
                  value={formData.room_type_id}
                  onChange={(e) => setFormData({ ...formData, room_type_id: e.target.value })}
                  className="w-full h-10 px-3 border"
                  style={{ borderColor: 'var(--border-medium)', borderRadius: 0 }}
                  data-testid="room-type-select"
                >
                  <option value="">-- Select Room Type --</option>
                  {roomTypes.map(rt => (
                    <option key={rt.id} value={rt.id}>
                      {rt.name} (${rt.base_rate_single}/night base)
                    </option>
                  ))}
                </select>
                <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                  Links this room to a billing configuration. <Link to="/admin/room-types" className="underline" style={{ color: 'var(--olive-primary)' }}>Manage Room Types</Link>
                </p>
              </div>

              <div>
                <Label htmlFor="description">Full Description *</Label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={4}
                  className="w-full p-3 border"
                  style={{ borderColor: 'var(--border-medium)', borderRadius: 0, fontFamily: "'Inter', sans-serif", fontSize: '14px' }}
                  data-testid="room-description-input"
                />
              </div>

              {/* Pricing */}
              <div>
                <Label className="mb-3 block">Pricing (per night)</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label htmlFor="price_usd" className="text-xs">USD *</Label>
                    <Input
                      id="price_usd"
                      type="number"
                      value={formData.price_usd}
                      onChange={(e) => setFormData({ ...formData, price_usd: e.target.value })}
                      required
                      style={{ borderRadius: 0 }}
                      data-testid="price-usd-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="price_zar" className="text-xs">ZAR</Label>
                    <Input
                      id="price_zar"
                      type="number"
                      value={formData.price_zar}
                      onChange={(e) => setFormData({ ...formData, price_zar: e.target.value })}
                      style={{ borderRadius: 0 }}
                    />
                  </div>
                  <div>
                    <Label htmlFor="price_gbp" className="text-xs">GBP</Label>
                    <Input
                      id="price_gbp"
                      type="number"
                      value={formData.price_gbp}
                      onChange={(e) => setFormData({ ...formData, price_gbp: e.target.value })}
                      style={{ borderRadius: 0 }}
                    />
                  </div>
                  <div>
                    <Label htmlFor="price_eur" className="text-xs">EUR</Label>
                    <Input
                      id="price_eur"
                      type="number"
                      value={formData.price_eur}
                      onChange={(e) => setFormData({ ...formData, price_eur: e.target.value })}
                      style={{ borderRadius: 0 }}
                    />
                  </div>
                </div>
              </div>

              {/* Room Details */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="max_occupancy">Max Occupancy</Label>
                  <Input
                    id="max_occupancy"
                    type="number"
                    value={formData.max_occupancy}
                    onChange={(e) => setFormData({ ...formData, max_occupancy: e.target.value })}
                    style={{ borderRadius: 0 }}
                  />
                </div>
                <div>
                  <Label htmlFor="beds">Bed Configuration</Label>
                  <Input
                    id="beds"
                    value={formData.beds}
                    onChange={(e) => setFormData({ ...formData, beds: e.target.value })}
                    placeholder="e.g., 1 King bed"
                    style={{ borderRadius: 0 }}
                  />
                </div>
                <div>
                  <Label htmlFor="size_sqm">Size (sqm)</Label>
                  <Input
                    id="size_sqm"
                    type="number"
                    value={formData.size_sqm}
                    onChange={(e) => setFormData({ ...formData, size_sqm: e.target.value })}
                    style={{ borderRadius: 0 }}
                  />
                </div>
              </div>

              {/* Images */}
              <div>
                <Label className="mb-3 block">Room Images *</Label>
                <ImageManager
                  images={formData.images}
                  onChange={(newImages) => setFormData({ ...formData, images: newImages })}
                  token={token}
                  maxImages={10}
                />
              </div>

              {/* Amenities */}
              <div>
                <Label htmlFor="amenities">Amenities (comma-separated)</Label>
                <Input
                  id="amenities"
                  value={formData.amenities}
                  onChange={(e) => setFormData({ ...formData, amenities: e.target.value })}
                  placeholder="WiFi, Air Conditioning, Mini Bar, Room Service"
                  style={{ borderRadius: 0 }}
                />
              </div>

              {/* Availability */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="available"
                  checked={formData.available}
                  onChange={(e) => setFormData({ ...formData, available: e.target.checked })}
                />
                <Label htmlFor="available" className="cursor-pointer">Available for booking</Label>
              </div>

              </div>

              {/* Fixed Footer */}
              <div className="flex-shrink-0 flex justify-between px-6 py-4" style={{ borderTop: '1px solid var(--border-light)' }}>
                <Button type="button" variant="outline" onClick={handleCancel} style={{ borderRadius: 0 }}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary" disabled={saving} data-testid="save-room-btn">
                  {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                  {editing ? 'Update Room' : 'Create Room'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Rooms List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="animate-spin" size={32} style={{ color: 'var(--olive-primary)' }} />
          </div>
        ) : rooms.length === 0 ? (
          <div className="p-12 text-center" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}>
            <p className="body-large mb-4" style={{ color: 'var(--text-secondary)' }}>
              No rooms found
            </p>
            <Button className="btn-primary" onClick={handleCreate}>
              <Plus size={16} className="mr-2" />
              Add Your First Room
            </Button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="rooms-list">
            {rooms.map((room) => (
              <div
                key={room.id}
                className="p-6 flex flex-col md:flex-row md:items-center gap-4"
                style={{ 
                  backgroundColor: 'var(--bg-secondary)', 
                  border: '1px solid var(--border-light)',
                  opacity: room.available ? 1 : 0.6
                }}
                data-testid={`room-${room.id}`}
              >
                {room.images?.[0] && (
                  <img 
                    src={room.images[0]} 
                    alt={room.name}
                    className="w-24 h-24 object-cover"
                    style={{ border: '1px solid var(--border-light)' }}
                  />
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="heading-3">{room.name}</h3>
                    {!room.available && (
                      <span className="px-2 py-1 text-xs" style={{ backgroundColor: '#ef4444', color: 'white' }}>
                        Hidden
                      </span>
                    )}
                    <span className="px-2 py-1 text-xs" style={{ backgroundColor: 'var(--olive-primary)', color: 'white' }}>
                      {room.images?.length || 0} images
                    </span>
                    {room.room_type_id && (
                      <span className="px-2 py-1 text-xs" style={{ backgroundColor: 'var(--interactive-base)', color: 'white' }}>
                        {roomTypes.find(rt => rt.id === room.room_type_id)?.name || 'Room Type'}
                      </span>
                    )}
                  </div>
                  <p className="body-small mb-2" style={{ color: 'var(--text-secondary)' }}>
                    {room.short_description || room.description?.slice(0, 100)}...
                  </p>
                  <p className="body-small" style={{ color: 'var(--text-light)' }}>
                    ${room.price_usd}/night • {room.max_occupancy} guests • {room.size_sqm} sqm
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={() => toggleAvailability(room)}
                    title={room.available ? 'Hide room' : 'Show room'}
                    style={{ borderRadius: 0 }}
                  >
                    {room.available ? <EyeOff size={16} /> : <Eye size={16} />}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={() => handleEdit(room)}
                    style={{ borderRadius: 0 }}
                    data-testid={`edit-${room.id}`}
                  >
                    <Edit size={16} />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={() => handleDelete(room.id)}
                    style={{ borderRadius: 0, borderColor: '#ef4444', color: '#ef4444' }}
                    data-testid={`delete-${room.id}`}
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminRoomsPage;
